import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ComponentSync, EditableDBItemV3, Grid, LL_H_C, LL_V_L, SimpleListAdapter, TS_Checkbox, TS_DropDown, TS_PropRenderer } from '@nu-art/thunderstorm-frontend/index';
import { referencableTypes, referencableTypes_LabelMap } from '@app/hcs-shared/hcs-v2';
import { Graph_Referencable } from '../Graph_Referencable/Graph_Referencable.js';
import './Editor_Referencable.scss';
import { DefaultEditor_InputText } from '@app/styles/frontend/ui-manager/ui-components';
import { MultiSelect_Referencable } from '../ui-components.js';
import { ModuleFE_Referencable } from '../ModuleFE_Referencable.js';
import { BadImplementationException, Filter, filterInstances, ImplementationMissingException, lastElement, MissingDataException, RuntimeModules, sortArray } from '@nu-art/ts-common';
import { ICONS } from '@app/styles';
import { DBDef_Tag } from '@app/hcs-shared';
import { DBDef_Variable } from '@app/hcs-shared';
import { DBDef_Value, resolveValueDisplay, valueType_EnumElement } from '@app/hcs-shared';
import { DropDown_DefaultCaret } from '@app/styles/frontend/ui/consts';
import React from 'react';
import { Editor_ReferencablePrototypes } from '../../../_enum/referencable-prototype/Editor_ReferencablePrototypes.js';
const relatedDBKeys = [DBDef_Tag.dbKey, DBDef_Variable.dbKey, DBDef_Value.dbKey];
export class Editor_Referencable extends ComponentSync {
    moduleMap;
    // ######################## Life Cycle ########################
    deriveStateFromProps(nextProps, state) {
        state.editables = [nextProps.editable];
        state.dbPointerDDItems ??= this.generateDBPointerDDItems();
        state.editables[0].setAutoSave(true);
        state.editables[0].setOnChanged(async () => this.forceUpdate());
        state.editables[0].setOnSaveCompleted(() => this.forceUpdate());
        return state;
    }
    // ######################## Logic ########################
    getEditable = (referencable) => {
        const editable = new EditableDBItemV3(referencable, ModuleFE_Referencable);
        editable.setAutoSave(true);
        editable.setOnChanged(async () => this.forceUpdate());
        editable.setOnSaveCompleted(() => this.forceUpdate());
        return editable;
    };
    generateModuleMap() {
        this.moduleMap = relatedDBKeys.reduce((map, dbKey) => {
            const module = RuntimeModules().find(module => module.dbDef?.dbKey === dbKey);
            if (!module)
                throw new BadImplementationException(`Could not find module for key ${dbKey}`);
            map[dbKey] = module;
            return map;
        }, {});
    }
    getModule(dbKey) {
        if (!this.moduleMap)
            this.generateModuleMap();
        return this.moduleMap[dbKey];
    }
    getDBPointerData(ref) {
        const module = this.getModule(ref.dbKey);
        if (!module)
            throw new MissingDataException(`Not collecting data for dbKey ${ref.dbKey}`);
        const item = module.cache.unique(ref.id);
        if (!item)
            throw new MissingDataException(`Could not find item for key ${ref.dbKey} and id ${ref.id}`);
        switch (ref.dbKey) {
            case DBDef_Tag.dbKey: {
                const tag = item;
                return {
                    label: tag.label,
                    dbName: module.dbDef.entityName,
                };
            }
            case DBDef_Variable.dbKey: {
                const variable = item;
                return {
                    label: variable.name,
                    dbName: module.dbDef.entityName,
                };
            }
            case DBDef_Value.dbKey: {
                const value = item;
                return {
                    label: resolveValueDisplay(value),
                    dbName: module.dbDef.entityName,
                };
            }
            default: {
                throw new ImplementationMissingException(`No DBPointer resolver for key ${ref.dbKey}`);
            }
        }
    }
    generateDBPointerDDItems() {
        const items = [];
        relatedDBKeys.forEach(dbKey => {
            const module = this.getModule(dbKey);
            let ids = [];
            if (dbKey === DBDef_Value.dbKey) {
                ids = filterInstances(module.cache.allMutable().map(i => i.type === valueType_EnumElement ? i._id : undefined));
            }
            else {
                ids = module.cache.allMutable().map(i => i._id);
            }
            items.push(...ids.map(id => ({
                dbKey,
                id,
                ...this.getDBPointerData({ id, dbKey }),
            })));
        });
        sortArray(items, item => item.label);
        return items;
    }
    getReferencableActions = (referencable) => {
        const actions = [];
        const currentEditable = lastElement(this.state.editables);
        if (referencable._id !== currentEditable?.item._id)
            actions.push({
                label: 'Edit',
                action: () => {
                    const editable = this.getEditable(referencable);
                    this.state.editables.push(editable);
                    this.forceUpdate();
                }
            });
        return actions;
    };
    // ######################## Render ########################
    render() {
        return _jsxs(LL_H_C, { className: 'editor__referencable', children: [this.render_Editor(), this.render_Graph()] });
    }
    render_Editor = () => {
        const editable = lastElement(this.state.editables);
        if (!editable)
            return;
        return _jsxs(Grid, { className: 'editor__referencable__editor', children: [this.render_Editor_Breadcrumbs(), this.render_Editor_Label(editable), this.render_Editor_Type(editable), this.render_Editor_DBRefs(editable), this.render_Editor_Extends(editable), this.render_Editor_Prototypes(editable), this.render_Editor_Properties(editable)] });
    };
    render_Graph = () => {
        const editable = lastElement(this.state.editables);
        if (!editable)
            return;
        const referencable = editable.item;
        return _jsx(Graph_Referencable, { referencable: referencable, getContextMenuActions: this.getReferencableActions });
    };
    // ######################## Render - Editor ########################
    render_Editor_Breadcrumbs = () => {
        const editables = this.state.editables;
        if (editables.length <= 1)
            return;
        const lastIndex = editables.length - 1;
        return _jsx(LL_H_C, { className: 'editor__referencable__breadcrumbs', children: editables.map((editable, index) => {
                return _jsxs(React.Fragment, { children: [_jsx(Button, { disabled: index === lastIndex, variant: 'text', onClick: () => {
                                const newEditables = this.state.editables.slice(0, index + 1);
                                this.setState({ editables: newEditables });
                            }, children: editable.item.label }), index !== lastIndex && '→'] }, index);
            }) });
    };
    render_Editor_Label = (editable) => {
        return _jsx(TS_PropRenderer.Vertical, { label: 'Label', children: _jsx(DefaultEditor_InputText, { editable: editable, prop: 'label' }) });
    };
    render_Editor_Type = (editable) => {
        const types = editable.editProp('type', []);
        return _jsx(TS_PropRenderer.Vertical, { label: 'Type', className: 'editor__referencable__type', children: _jsx(LL_H_C, { children: referencableTypes.map((type, index) => {
                    const checked = types.item.includes(type);
                    return _jsx(TS_Checkbox, { checked: checked, onCheck: () => {
                            if (checked)
                                types.removeArrayItem(index);
                            else
                                types.updateArrayAt(type);
                        }, children: referencableTypes_LabelMap[type] }, type);
                }) }) });
    };
    render_Editor_DBRefs = (editable) => {
        const refs = editable.editProp('dbRefs', []);
        const existingIds = refs.item.map(item => item.id);
        const adapter = SimpleListAdapter(this.state.dbPointerDDItems, item => _jsxs(_Fragment, { children: [_jsx("div", { children: item.item.label }), _jsx("div", { children: item.item.dbName })] }));
        const filter = new Filter(item => [item.label]).setRegexp(false);
        return _jsx(TS_PropRenderer.Vertical, { label: 'DBRefs', className: 'span-row', children: _jsxs(LL_H_C, { className: 'editor__referencable__editor__db-refs', children: [refs.item.map((ref, index) => {
                        if (!ref)
                            return;
                        return this.render_Editor_DBRef(ref, () => refs.removeArrayItem(index));
                    }), _jsx(TS_DropDown, { adapter: adapter, selected: undefined, className: 'dd__db-refs', filter: filter, limitItems: 50, placeholder: 'Add DBRef', caret: DropDown_DefaultCaret, queryFilter: item => !existingIds.includes(item.id), onSelected: item => {
                            refs.updateArrayAt({ id: item.id, dbKey: item.dbKey });
                        } })] }) });
    };
    render_Editor_DBRef = (ref, onDelete) => {
        const data = this.state.dbPointerDDItems.find(item => item.id === ref.id);
        return _jsxs(LL_H_C, { className: 'editor__referencable__editor__db-ref', children: [_jsx(ICONS.x.component, { onClick: onDelete }), _jsx("div", { className: 'editor__referencable__editor__db-ref__label', children: data?.label ?? 'N/A' }), _jsx("div", { className: 'editor__referencable__editor__db-ref__tag', children: data?.dbName ?? 'Unknown' })] }, ref.id);
    };
    render_Editor_Extends = (editable) => {
        return _jsx(TS_PropRenderer.Vertical, { label: 'Extends', className: 'span-row', children: _jsx(MultiSelect_Referencable, { editable: editable, prop: 'extends' }) });
    };
    render_Editor_Prototypes = (editable) => {
        const prototypes = editable.editProp('prototypes', []);
        return _jsx(TS_PropRenderer.Vertical, { label: 'Prototypes', className: 'span-row', children: _jsx(Editor_ReferencablePrototypes, { editable: prototypes }) });
    };
    render_Editor_Properties = (editable) => {
        const properties = editable.editProp('properties', {});
        const propIds = ModuleFE_Referencable.inferPropIds(editable.item);
        return _jsx(TS_PropRenderer.Vertical, { label: 'Properties', className: 'editor__referencable__properties span-row', children: _jsx(LL_V_L, { children: propIds.map(propId => {
                    const prop = ModuleFE_Referencable.cache.unique(propId);
                    if (!prop)
                        throw new MissingDataException(`Could not find referencable for id ${propId}`);
                    return _jsx(TS_PropRenderer.Vertical, { label: prop.label, children: _jsx(MultiSelect_Referencable, { editable: properties, prop: propId, itemFilter: (itemId) => {
                                const item = ModuleFE_Referencable.cache.unique(itemId);
                                if (!item)
                                    throw new MissingDataException(`Could not find referencable for id ${itemId}`);
                                return item.extends.includes(propId);
                            } }) }, propId);
                }) }) });
    };
}
//# sourceMappingURL=Editor_Referencable.js.map